import { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { debounce } from 'throttle-debounce';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { MealSearch } from '@/lib/local-api/meal/meal-search';
import { SearchMealsDocument, type Meal } from '~/graphql/generated/graphql';

export interface MealSelectorSearchProps {
  /**
   * Called when search results change
   */
  onResultsChange?: (results: Meal[]) => void;
}

export const MealSelectorSearch = ({
  onResultsChange,
}: MealSelectorSearchProps) => {
  const { t } = useTranslation('meal');
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  // Debounce the input so the query fires only once the user pauses typing —
  // not on every keystroke (a true trailing-edge debounce, unlike a throttle).
  const debouncedSearch = useMemo(
    () =>
      debounce(500, (value: string) => {
        setDebouncedSearchValue(value);
      }),
    [],
  );

  // Push each keystroke into the debouncer.
  useEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);

  // Cancel any pending debounced call on unmount to avoid a late setState.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // GraphQL query to get meals directly. Skipped until the (trimmed) input
  // reaches the minimum-character gate so single characters don't hit the API.
  const {
    data: searchData,
    loading: isSearching,
    error: searchError,
  } = useQuery(SearchMealsDocument, {
    variables: {
      query: {
        search: MealSearch.normalize(debouncedSearchValue),
        limit: 50,
        includeMine: true,
      },
    },
    skip: !MealSearch.shouldSearch(debouncedSearchValue),
  });

  // Notify parent component about results changes
  useEffect(() => {
    const items = searchData?.meals?.items ?? [];
    if (onResultsChange) {
      onResultsChange(items as Meal[]);
    }
  }, [searchData, onResultsChange]);

  const hasError = Boolean(searchError);

  return (
    <View>
      <Input
        testID="meal-search-input"
        placeholder={t('mealSelector.searchPlaceholder')}
        value={searchValue}
        onChangeText={setSearchValue}
        className="mb-2"
      />

      {/* Error state */}
      {hasError && (
        <Text className="text-sm text-destructive">
          {t('common.error.generic')}
        </Text>
      )}
    </View>
  );
};
